import { Maybe } from 'graphql/jsutils/Maybe';
import { numericFormatter } from 'react-number-format';
import * as Yup from 'yup';

import { CreativeType } from '../../../apis/graphql/types';
import { DEFAULT_PART_LIST } from '../../../components/ScheduleCalendar/constants';
import { isKerv } from '../../../utils/isKerv';
import { isValidUrl } from '../../../utils/url';
import { LineItemAdType, LineItemForm, LineItemFormFieldsNames } from './types';

export const LINE_ITEM_FORM_SAVE_STATUS = 'save';
export const LINE_ITEM_FORM_FAIL_SAVING_STATUS = 'fail';

export const ANONIMOUS_USER_ID = 'anonymousUser';

export const LINE_ITEM_INITIAL_VALUES: LineItemForm = {
  id: '',
  name: 'Line Item 1',
  startTime: null,
  endTime: null,
  dateRange: [],
  budget: 0,
  calculatedCost: '',
  ages: [],
  platform: [],
  dayParting: DEFAULT_PART_LIST,
  pacing: '',
  frequency: '',
  duration: '',
  contentTargetings: [],
  locationTargetings: [],
  publishers: null,
  retargetings: [],
  audiences: [],
  updatedAt: undefined,
  allowMatureContent: true,
  zipCodes: [],
  status: '',
  ads: [],
  weight: [],
  isPreemptible: false,
};

export const NAME: LineItemFormFieldsNames = 'name';
export const DATE_RANGE: LineItemFormFieldsNames = 'dateRange';
export const BUDGET: LineItemFormFieldsNames = 'budget';
export const AGES: LineItemFormFieldsNames = 'ages';
export const PLATFORM: LineItemFormFieldsNames = 'platform';
export const PACING: LineItemFormFieldsNames = 'pacing';
export const FREQUENCY: LineItemFormFieldsNames = 'frequency';
export const DURATION: LineItemFormFieldsNames = 'duration';
export const CONTENT_TARGETINGS: LineItemFormFieldsNames = 'contentTargetings';
export const LOCATION_TARGETINGS: LineItemFormFieldsNames = 'locationTargetings';
export const RETARGETINGS: LineItemFormFieldsNames = 'retargetings';
export const START_TIME: LineItemFormFieldsNames = 'startTime';
export const END_TIME: LineItemFormFieldsNames = 'endTime';
export const AUDIENCES: LineItemFormFieldsNames = 'audiences';
export const ALLOW_MATURE_CONTENT: LineItemFormFieldsNames = 'allowMatureContent';
export const ESTIMATED_DAILY_IMPRESSIONS: LineItemFormFieldsNames = 'estimatedDailyImpressions';
export const TARGET_IMPRESSIONS: LineItemFormFieldsNames = 'targetImpressions';
export const UNIT_COST: LineItemFormFieldsNames = 'unitCost';
export const ZIPCODES: LineItemFormFieldsNames = 'zipCodes';
export const DAY_PARTING: LineItemFormFieldsNames = 'dayParting';
export const ADS: LineItemFormFieldsNames = 'ads';
export const EDITING_TAG: LineItemFormFieldsNames = 'editingTag';
export const WEIGHT: LineItemFormFieldsNames = 'weight';
export const IS_PRE_EMPTIBLE: LineItemFormFieldsNames = 'isPreemptible';
export const PUBLISHERS: LineItemFormFieldsNames = 'publishers';

export enum EDITING_TAG_FIELD {
  name = 'editingTag.name',
  type = 'editingTag.type',
  url = 'editingTag.url',
  configuredUrl = 'editingTag.configuredUrl',
  replacedValues = 'editingTag.replacedValues',
}

export const LineItemFormValidationSchema = (
  adAccountApprovedForTerms: boolean | null | undefined,
  kervBugetThreshold: number = 500
) =>
  Yup.object().shape({
    name: Yup.string()
      .matches(/^[a-zA-Z0-9_ -]{2,120}$/, 'Enter a valid line item name')
      .required('Enter line item name'),
    budget: Yup.number()
      .when(ADS, {
        is: (ads: Array<Maybe<LineItemAdType>>) => ads?.length > 0 && isKerv(ads[0]),
        then: Yup.number().min(
          kervBugetThreshold,
          `Budget must be at least ${numericFormatter(kervBugetThreshold.toString(), { thousandSeparator: true })}$`
        ),
        otherwise: Yup.number().min(500, 'Budget must be at least $500'),
      })
      .max(
        adAccountApprovedForTerms ? 1000000 : 100000,
        `Your campaign budget can't exceed ${
          adAccountApprovedForTerms ? '$1,000,000.00' : '$100,000.00'
        }. If you'd like to start a larger campaign, contact us via the Contact Us form.`
      ),
    [EDITING_TAG]: Yup.object()
      .shape({
        name: Yup.string()
          .matches(/^[a-zA-Z0-9 \-_!@#$%^&*()+=<>?/\\{}[\]~`|:;"',.]{1,255}$/, 'Enter a valid tag name')
          .required('Enter tag name'),
        type: Yup.string()
          .matches(/^[a-zA-Z0-9 ]{2,120}$/, 'Enter a valid type')
          .required('Enter type'),
        url: Yup.string().test('isUrlValid', 'Enter a valid URL', (value) => isValidUrl(value || '')),
        configuredUrl: Yup.string().required('Enter a valid url'),
      })
      .optional()
      .default(undefined),
    weight: Yup.array().when(ADS, {
      is: (ads: Array<Maybe<LineItemAdType>>) =>
        ads?.length > 0 && ads[0]?.creative?.creativeType !== CreativeType.VastVideo,
      then: Yup.array()
        .of(Yup.number().min(0.0000001, '').max(1, ''))
        .test('sum-of-percentage', 'Wrong value', (weights) => {
          const total = weights?.reduce((acc, current) => (acc ?? 0) + (current ?? 0), 0);
          return total === 1;
        })
        .required(''),
    }),
    [PUBLISHERS]: Yup.string().required('Choose publisher type of line item'),
  });

export const AdTagsValidationSchema = Yup.object().shape({
  [EDITING_TAG]: Yup.object()
    .shape({
      name: Yup.string()
        .matches(/^[a-zA-Z0-9 \-_!@#$%^&*()+=<>?/\\{}[\]~`|:;"',.]{1,255}$/, 'Enter a valid tag name')
        .required('Enter tag name'),
      type: Yup.string()
        .matches(/^[a-zA-Z0-9 ]{2,120}$/, 'Enter a valid type')
        .required('Enter type'),
      url: Yup.string().test('isUrlValid', 'Enter a valid URL', (value) => isValidUrl(value || '')),
      configuredUrl: Yup.string().required('Enter a valid url'),
    })
    .optional()
    .default(undefined),
});

export enum SETUP_SECTION_IDS {
  setup = `setupSection`,
  name = `lineItemField_name`,
  dateRange = `lineItemField_DateRange`,
  budget = `lineItemField_BUDGET`,
}

export enum TARGETING_SECTION_IDS {
  targeting = `targetingSection`,
  location = `lineItemField_Location`,
  gender = `lineItemField_Gender`,
  ageGroups = `lineItemField_AgeGroups`,
  audiences = `lineItemField_Audiences`,
  retargeting = `lineItemField_Retargeting`,
  platform = `lineItemField_Platform`,
  contentTargeting = `lineItemField_ContentTargeting`,
}

export enum DELIVERY_SECTION_IDS {
  delivery = `deliverySection`,
  daypart = `lineItemField_Daypart`,
  pacing = `lineItemField_Pacing`,
  frequency = `lineItemField_Frequency`,
}

export enum ADS_SECTION_IDS {
  ads = `adsSection`,
  adUpload = `lineItemField_AdUpload`,
  adWeight = `lineItemField_AdWeight`,
}

export const DROPDOWN_FORM_LIST = [
  'retargetingAudiencesDropdown',
  'contentTargetingDropdown',
  'locationTargetingDropdown',
];
